import { forwardRef, useMemo } from 'react'
import { format } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import {
  QUADRANT_LOWER_LEFT_COLOUR,
  QUADRANT_LOWER_RIGHT_COLOUR,
  QUADRANT_POINT_COLOUR,
  QUADRANT_UPPER_LEFT_COLOUR,
  QUADRANT_UPPER_RIGHT_COLOUR,
} from '../constants/riskProfile.constants'
import type { RiskProfileAsset } from '../types/riskProfileAssets.types'
import { formatDollarCompact } from '../utils/riskProfile.utils'
import { buildQuadrantPoints } from '../utils/riskProfileQuadrant.utils'
import { useNavigate, useSearchParams } from 'react-router-dom'

export interface RiskProfileAssetRiskQuadrantChartProps {
  data: RiskProfileAsset[]
}

const RiskProfileAssetRiskQuadrantChart = forwardRef<
  ReactECharts,
  RiskProfileAssetRiskQuadrantChartProps
>(({ data }, ref) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const currencySymbol = useAtomValue(currencySymbolAtom)
  const points = useMemo(() => buildQuadrantPoints(data), [data])

  const bounds = useMemo(() => {
    if (points.length === 0) return { xMax: 1, yMax: 1 }
    return {
      xMax: Math.max(...points.map((p) => p.replacementCost)) * 1.05,
      yMax: Math.max(...points.map((p) => p.damageCost)) * 1.05,
    }
  }, [points])

  const option = useMemo(
    () => ({
      grid: { left: 72, right: 32, top: 24, bottom: 56 },
      tooltip: {
        trigger: 'item',
        formatter: (params: { data: { value: [number, number]; assetId?: string } }) => {
          const [x, y] = params.data.value
          const title = params.data.assetId
            ? `<div style="font-weight:600;margin-bottom:4px">${format.encodeHTML(params.data.assetId)}</div>`
            : ''
          return (
            `${title}` +
            `<div>Replacement cost: <span style="font-weight:600">${formatDollarCompact(x, currencySymbol)}</span></div>` +
            `<div>Damage cost: <span style="font-weight:600">${formatDollarCompact(y, currencySymbol)}</span></div>`
          )
        },
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: bounds.xMax,
        name: 'Element Value',
        nameLocation: 'middle',
        nameGap: 32,
        axisLabel: { formatter: (v: number) => formatDollarCompact(v, currencySymbol) },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: bounds.yMax,
        name: 'Damage Cost',
        nameLocation: 'middle',
        nameGap: 56,
        axisLabel: { formatter: (v: number) => formatDollarCompact(v, currencySymbol) },
      },
      series: [
        {
          name: 'Assets',
          type: 'scatter',
          symbolSize: 8,
          data: points.map((p) => ({
            value: [p.replacementCost, p.damageCost],
            assetId: p.assetId,
            asset_type: p.asset_type,
            aggregate_id: p.aggregate_id,
          })),
          itemStyle: { color: QUADRANT_POINT_COLOUR },
          markArea: {
            silent: true,
            itemStyle: { borderWidth: 0 },
            data: [
              [
                {
                  xAxis: 0,
                  yAxis: bounds.yMax / 2,
                  itemStyle: { color: QUADRANT_UPPER_LEFT_COLOUR },
                },
                { xAxis: bounds.xMax / 2, yAxis: bounds.yMax },
              ],
              [
                {
                  xAxis: bounds.xMax / 2,
                  yAxis: bounds.yMax / 2,
                  itemStyle: { color: QUADRANT_UPPER_RIGHT_COLOUR },
                },
                { xAxis: bounds.xMax, yAxis: bounds.yMax },
              ],
              [
                {
                  xAxis: 0,
                  yAxis: 0,
                  itemStyle: { color: QUADRANT_LOWER_LEFT_COLOUR },
                },
                { xAxis: bounds.xMax / 2, yAxis: bounds.yMax / 2 },
              ],
              [
                {
                  xAxis: bounds.xMax / 2,
                  yAxis: 0,
                  itemStyle: { color: QUADRANT_LOWER_RIGHT_COLOUR },
                },
                { xAxis: bounds.xMax, yAxis: bounds.yMax / 2 },
              ],
            ],
          },
        },
      ],
    }),
    [points, bounds, currencySymbol],
  )

  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">No comparable assets for this selection.</p>
  }

  return (
    <ReactECharts
      ref={ref}
      option={option}
      notMerge
      onEvents={{
        click: (params: { data: { asset_type: string; aggregate_id: string } }) => {
          const { asset_type, aggregate_id } = params.data
          navigate(`/app/risk/profile/element/${asset_type}/${aggregate_id}?${searchParams}`)
        },
      }}
    />
  )
})

RiskProfileAssetRiskQuadrantChart.displayName = 'RiskProfileAssetRiskQuadrantChart'

export default RiskProfileAssetRiskQuadrantChart
